import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getPagePermissions, savePagePermissions } from '../../services/pagePermissionsApi';
import { Shield, Save, AlertCircle } from 'lucide-react';

const PAGE_OPTIONS = [
  { key: 'calendar', label: 'Calendar' },
  { key: 'featureFlags', label: 'Feature Flags' },
  { key: 'queryVault', label: 'QueryVault' },
  { key: 'azureVariables', label: 'Azure Variables' },
  { key: 'tenantProvisioning', label: 'Tenant Provisioning' },
  { key: 'tenantOnboarding', label: 'Tenant Onboarding' },
  { key: 'envScanner', label: 'Env Scanner' },
  { key: 'deployments', label: 'Deployments' },
  { key: 'inactiveUsers', label: 'Inactive Users' },
  { key: 'healthCheck', label: 'Health Check' },
  { key: 'urlRegistry', label: 'URL Registry' },
  { key: 'avalaraLicensing', label: 'Avalara Licensing' },
  { key: 'eventDashboard', label: 'Event Dashboard' },
  { key: 'trainingRevert', label: 'Training Revert' },
  { key: 'databaseExport', label: 'Database Export' },
];

const DEFAULT_PERMISSIONS = {
  calendar: true,
  featureFlags: true,
  queryVault: true,
  azureVariables: false,
  tenantProvisioning: false,
  tenantOnboarding: false,
  envScanner: false,
  deployments: false,
  inactiveUsers: true,
  healthCheck: true,
  urlRegistry: true,
  avalaraLicensing: false,
  eventDashboard: false,
  trainingRevert: false,
  databaseExport: false,
};

const AccessControl = () => {
  const { logout } = useAuth();
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    getPagePermissions()
      .then((data) => setPermissions({ ...DEFAULT_PERMISSIONS, ...data }))
      .catch((err) => {
        if (err.status === 401) {
          logout();
        } else {
          setError(err.message || 'Failed to load permissions');
          setPermissions({ ...DEFAULT_PERMISSIONS });
        }
      })
      .finally(() => setLoading(false));
  }, [logout]);

  const handleToggle = (key) => {
    setPermissions((prev) => ({ ...prev, [key]: !prev[key] }));
    setSuccess(false);
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      await savePagePermissions(permissions);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px] text-gray-600 dark:text-gray-400">
        <div className="w-6 h-6 border-2 border-gray-300 dark:border-slate-600 border-t-blue-600 rounded-full animate-spin" />
        <span className="ml-3">Loading permissions...</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-xl flex items-center justify-center">
          <Shield className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Access Control</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Configure which pages are visible to innova.su users
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-4 mb-6 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-red-700">
          <AlertCircle size={20} className="shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-3 p-4 mb-6 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg text-green-700">
          <Save size={20} className="shrink-0" />
          <span>Permissions saved successfully</span>
        </div>
      )}

      <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 mb-6">
        <div className="p-4 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-950 dark:text-gray-100">Pages visible to innova.su</div>
        <div className="divide-y divide-gray-200 dark:divide-slate-700">
          {PAGE_OPTIONS.map(({ key, label }) => (
            <div
              key={key}
              className="flex items-center justify-between px-4 py-4 hover:bg-gray-50/50 dark:hover:bg-slate-800/50"
            >
              <span className="font-medium text-gray-800 dark:text-gray-200">{label}</span>
              <button
                type="button"
                role="switch"
                aria-checked={permissions[key]}
                onClick={() => handleToggle(key)}
                className={`
                  relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent
                  transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                  ${permissions[key] ? 'bg-blue-600' : 'bg-gray-200 dark:bg-slate-700'}
                `}
              >
                <span
                  className={`
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                    transition duration-200 ease-in-out
                    ${permissions[key] ? 'translate-x-5' : 'translate-x-1'}
                  `}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {saving ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Save size={18} />
            Save Changes
          </>
        )}
      </button>
    </div>
  );
};

export default AccessControl;
